import React, { useState, useEffect, useMemo, useRef } from 'react';

const ROLE_META = {
  END_USER: {
    id: 'END_USER',
    label: 'Collector / Hunter',
    badge: 'End User',
    summary: 'Personal Vault, 3D Trophy Coin inspection, Trophy Brag Card sharing, and smartphone RFID/NFC tap scanning.',
    quickQuestions: [
      'How do I scan a physical Trophy Coin with my phone?',
      'How do I view and rotate my 3D Trophy Coins in the Vault?',
      'How do I download or share my Trophy Brag Card?',
      'How do I enable Two-Factor Authentication (2FA) on my account?'
    ],
    guides: [
      {
        title: 'Scanning Physical RFID Trophy Coins',
        category: 'RFID & Mobile',
        description: 'Tap your physical Trophy Coin against your smartphone to launch its verified 3D digital twin and harvest certificate.',
        steps: [
          'Wake your smartphone screen (unlock your phone on Android).',
          'For iPhone (XR / XS / 11 / 12 / 13 / 14 / 15 / 16+): Hold the top back edge of the iPhone steady against the coin for 1–2 seconds.',
          'For Android (Samsung, Pixel, OnePlus, etc.): Ensure NFC is enabled in Settings, then hold the center-back of the phone over the coin.',
          'Tap the notification banner that appears to open the verified Trophy Card and save it to your Personal Vault.'
        ],
        actionTab: 'support_rfid',
        actionLabel: 'Check Phone RFID Compatibility'
      },
      {
        title: 'Exploring Your Personal Vault & 3D Coins',
        category: 'Personal Vault',
        description: 'Inspect your collected coins in interactive 3D and view official SCI or Rowland Ward scoring details.',
        steps: [
          'Click "Personal Vault" in the left navigation bar.',
          'Select any Trophy Coin from your collection grid.',
          'Drag on the 3D coin preview to rotate both front and back metallic reliefs and rim engravings.',
          'Switch between the 3D Coin view and the Trophy Brag Card view to see full harvest measurements and outfitter verification.'
        ],
        actionTab: 'vault',
        actionLabel: 'Open Personal Vault'
      },
      {
        title: 'Sharing & Downloading Trophy Brag Cards',
        category: 'Sharing',
        description: 'Share high-resolution digital Trophy Cards with friends or social media.',
        steps: [
          'Open your coin inside "Personal Vault".',
          'Switch to the "Trophy Card" view.',
          'Use the Share or Download buttons to export a high-resolution image or copy your verified public trophy link.'
        ],
        actionTab: 'vault',
        actionLabel: 'Go to Personal Vault'
      },
      {
        title: 'Account Security & Two-Factor Authentication',
        category: 'Security',
        description: 'Keep your Trophy Vault secure by updating your password and enabling 2FA.',
        steps: [
          'Click your profile avatar in the top-right header and choose "My Profile & Account".',
          'Update your contact details, avatar, or account password.',
          'Click "Enable Two-Factor Authentication" to add an extra layer of login security.'
        ],
        actionTab: 'profile',
        actionLabel: 'Open My Profile'
      }
    ]
  },
  OPERATOR: {
    id: 'OPERATOR',
    label: 'Outfitter / Operator',
    badge: 'Operator',
    summary: 'Quick Minting client harvests, customizing lodge 3D coin faces & Trophy Cards, and tracking physical coin orders.',
    quickQuestions: [
      'How do I mint a new Trophy Coin for a client harvest?',
      'How do I customize my lodge\'s 3D coin face and Trophy Card?',
      'How do I track physical coin manufacturing and shipping orders?',
      'How can I check if my client\'s phone supports RFID coin scanning?'
    ],
    guides: [
      {
        title: 'Quick Minting a Client Trophy Coin',
        category: 'Minting',
        description: 'Issue a bespoke physical & digital Trophy Coin for a hunter in under two minutes.',
        steps: [
          'Click "Quick Mint" in the top bar (or "Coin Mint" in the sidebar).',
          'Choose the harvested species and enter the client\'s name, email, harvest date, and location.',
          'Enter official trophy measurements (SCI / Rowland Ward) and upload field harvest photos.',
          'Preview the live 3D Coin and Trophy Card, then submit the mint order.'
        ],
        actionTab: 'mint',
        actionLabel: 'Launch Quick Mint'
      },
      {
        title: 'Tracking Orders & Production Status',
        category: 'Fulfillment',
        description: 'Monitor physical coin engraving, RFID encoding, and shipping progress.',
        steps: [
          'Open "Orders" from the left sidebar.',
          'Filter orders by status (Pending, In Production, Encoded, Shipped, Delivered).',
          'Click any order to review the print-ready proof, serial number, and shipping details.'
        ],
        actionTab: 'production',
        actionLabel: 'View Orders'
      },
      {
        title: 'Customizing Lodge Coins & Trophy Cards',
        category: 'Branding & Design',
        description: 'Tailor how your outfitter\'s 3D coins and digital Trophy Cards appear to clients.',
        steps: [
          'Select "Coins" or "Trophy Cards" in the left sidebar.',
          'Choose a template or create a custom operator layout featuring your lodge logo and rim text.',
          'Save your template so it automatically populates during Quick Mint.'
        ],
        actionTab: 'operator_coins',
        actionLabel: 'Open Lodge Coins'
      },
      {
        title: 'Managing Your Outfitter Organisation',
        category: 'Organisation',
        description: 'Update your lodge branding, contact details, address, and staff access.',
        steps: [
          'Click "Organisation" in the left sidebar.',
          'Upload your high-resolution lodge logo and verify your website and contact details.',
          'Save changes to ensure all newly minted Trophy Cards reflect your latest branding.'
        ],
        actionTab: 'organisation',
        actionLabel: 'Open Organisation'
      }
    ]
  },
  AGENT: {
    id: 'AGENT',
    label: 'Partner / Sales Agent',
    badge: 'Agent',
    summary: 'Onboarding outfitter companies, sharing partner referral links, demonstrating Quick Mint, and tracking commissions.',
    quickQuestions: [
      'How do I invite and onboard a new outfitter company?',
      'Where do I find my partner referral link and commission stats?',
      'How do I demonstrate Quick Mint to a prospective lodge?',
      'How do I verify if an outfitter\'s phone can scan RFID Trophy Coins?'
    ],
    guides: [
      {
        title: 'Using the Agent Partner Portal & Referral Links',
        category: 'Partner Portal',
        description: 'Share your unique registration link to onboard outfitters and track partner performance.',
        steps: [
          'Click "Home" in the top bar or "Agent Partner Portal" in the sidebar.',
          'Copy your unique Partner Invitation Link or send direct email invites to outfitters.',
          'Track active lodges, minted coin volume, and commission summaries from your dashboard.'
        ],
        actionTab: 'overview',
        actionLabel: 'Open Agent Portal'
      },
      {
        title: 'Managing Assigned Outfitter Companies',
        category: 'Company Management',
        description: 'View and assist outfitter organizations registered under your agency.',
        steps: [
          'Open "Company Manager" in the left sidebar.',
          'Inspect each outfitter\'s profile, branding readiness, and recent minting activity.',
          'Assist outfitters with configuring their initial logo and coin templates.'
        ],
        actionTab: 'companies',
        actionLabel: 'Open Company Manager'
      },
      {
        title: 'Live Demo: Quick Mint & RFID Tap Check',
        category: 'Sales Demo',
        description: 'Run a live demonstration of 3D coin minting and physical RFID smartphone scanning.',
        steps: [
          'Open "Quick Mint" to show how fast a lodge can create a 3D coin and Trophy Card after a hunt.',
          'Open the "Phone RFID / NFC Checker" on this Support page to show clients how their iPhone or Android scans the physical coin.'
        ],
        actionTab: 'mint',
        actionLabel: 'Open Quick Mint'
      }
    ]
  },
  TENANT_ADMIN: {
    id: 'TENANT_ADMIN',
    label: 'Tenant Administrator',
    badge: 'Tenant Admin',
    summary: 'Full tenant theme styling, 3D Design Studio, Production & RFID encoding queue, Company/User management, and Agent network.',
    quickQuestions: [
      'How do I customize all colors and buttons using the Tenant Styler?',
      'How do I create tenant-wide vs operator-specific 3D coin templates?',
      'How do I manage the Production queue and encode RFID chips?',
      'How do I add or manage Outfitter Companies and Users?'
    ],
    guides: [
      {
        title: 'Styling Your Platform with the Tenant Styler',
        category: 'Theme & Styling',
        description: 'Control every background, card, button, badge, and text color across your tenant.',
        steps: [
          'Open "Site Settings" from the left sidebar or top-right profile menu.',
          'Adjust Primary Color, Accent Color, Card & Panel backgrounds, and Button Secondary colors.',
          'Use the contrast controls to ensure high readability in both dark and light palettes, then click Save.'
        ],
        actionTab: 'settings',
        actionLabel: 'Open Site Settings & Styler'
      },
      {
        title: '3D Coin & Trophy Card Design Studio (Scopes)',
        category: 'Design Studio',
        description: 'Build reusable 3D Coin Faces and Trophy Cards across Tenant or Operator scopes.',
        steps: [
          'Open "Design Studio" in the left sidebar.',
          'Use the Scope Selector at the top to design for the entire Tenant or scope down to a specific Operator.',
          'Note: Tenant-wide coin faces remain visible even when previewing an Operator scope so shared faces never disappear.',
          'Upload custom 3D depth reliefs and textures in "Assets / Media".'
        ],
        actionTab: 'design',
        actionLabel: 'Open Design Studio'
      },
      {
        title: 'Production Queue, Print Proofs & RFID Encoding',
        category: 'Production',
        description: 'Process physical coin orders, export print-ready artwork, and encode NDEF RFID URLs.',
        steps: [
          'Open "Production" in the left sidebar.',
          'Select an order to generate calibrated print/engraving proofs and certificates.',
          'Program the coin\'s 13.56 MHz NTAG RFID chip with its unique `/c/<serial>` URL and mark the order Encoded/Shipped.'
        ],
        actionTab: 'production',
        actionLabel: 'Open Production Queue'
      },
      {
        title: 'Managing Users, Outfitters & Agent Network',
        category: 'Administration',
        description: 'Administer user accounts, role permissions, outfitter companies, and regional sales agents.',
        steps: [
          'Use "User Manager" to invite users, assign roles (End User, Operator, Agent, Tenant Admin), or reset credentials.',
          'Use "Company Manager" to configure outfitter organizations and assign operators.',
          'Use "Agent Network" to oversee partner agents and territory assignments.'
        ],
        actionTab: 'users',
        actionLabel: 'Open User Manager'
      }
    ]
  },
  GLOBAL_ADMIN: {
    id: 'GLOBAL_ADMIN',
    label: 'Global Administrator',
    badge: 'Global Admin',
    summary: 'Multi-tenant administration, global 3D asset libraries, species/scoring Data Manager, and cross-tenant production.',
    quickQuestions: [
      'How do I manage tenant themes, custom domains, and Gemini AI keys?',
      'How do I publish Global 3D Coin Faces and Trophy Card templates?',
      'How do I manage species and scoring formulas in Data Manager?',
      'How do I test Web NFC / RFID encoding across devices?'
    ],
    guides: [
      {
        title: 'Multi-Tenant Configuration & AI Keys',
        category: 'Global Settings',
        description: 'Configure tenant branding, custom domains, SMTP mail servers, and Gemini AI keys.',
        steps: [
          'Open "Site Settings" from the sidebar or profile dropdown.',
          'Switch or configure tenant settings, theme palettes, and custom domain bindings.',
          'Add a Gemini API Key to enable live generative AI capabilities in the Design Studio and Support Assistant.'
        ],
        actionTab: 'settings',
        actionLabel: 'Open Site Settings'
      },
      {
        title: 'Global Design Studio & Asset Library',
        category: 'Design & Assets',
        description: 'Publish master 3D coin templates and relief assets shared across tenants and operators.',
        steps: [
          'Upload master 3D depth maps, metallic rims, and animal reliefs in "Assets / Media".',
          'Open "Design Studio" to assemble Global or Tenant-scoped 3D coin faces and Trophy Card layouts.'
        ],
        actionTab: 'design',
        actionLabel: 'Open Design Studio'
      },
      {
        title: 'Species & Scoring Data Manager',
        category: 'System Data',
        description: 'Maintain species catalogs, SCI / Rowland Ward measurement fields, and system records.',
        steps: [
          'Click "Data Manager" in the left sidebar.',
          'Add or update species records, scoring thresholds, and reference metadata.'
        ],
        actionTab: 'data_manager',
        actionLabel: 'Open Data Manager'
      }
    ]
  }
};

const PHONE_DATABASE = [
  // Apple iPhone
  {
    brand: 'Apple',
    model: 'iPhone 16 / 16 Plus / 16 Pro / 16 Pro Max',
    years: '2024–Present',
    rfidSupported: true,
    mode: 'Native Background NFC (No App Needed)',
    frequency: '13.56 MHz HF RFID / NFC (ISO 14443A / NTAG)',
    antennaLocation: 'Top back edge (beside the rear camera plateau)',
    antennaZone: 'top',
    instructions: 'Simply wake the screen and hold the top back edge of your iPhone within 1–2 cm of the Trophy Coin. A notification banner will appear automatically.'
  },
  {
    brand: 'Apple',
    model: 'iPhone 15 / 15 Plus / 15 Pro / 15 Pro Max',
    years: '2023',
    rfidSupported: true,
    mode: 'Native Background NFC (No App Needed)',
    frequency: '13.56 MHz HF RFID / NFC (ISO 14443A / NTAG)',
    antennaLocation: 'Top back edge (beside the rear camera bump)',
    antennaZone: 'top',
    instructions: 'Wake the iPhone screen and tap the top edge of the phone against the center of the Trophy Coin.'
  },
  {
    brand: 'Apple',
    model: 'iPhone 14 / 13 / 12 Series (All Models including Mini, Plus, Pro, Max)',
    years: '2020–2022',
    rfidSupported: true,
    mode: 'Native Background NFC (No App Needed)',
    frequency: '13.56 MHz HF RFID / NFC (ISO 14443A / NTAG)',
    antennaLocation: 'Top back edge of the phone',
    antennaZone: 'top',
    instructions: 'Wake your screen (no need to open an app) and hold the top edge of the iPhone over the Trophy Coin for 1 second.'
  },
  {
    brand: 'Apple',
    model: 'iPhone 11 / XS / XS Max / XR / SE (2nd & 3rd Gen)',
    years: '2018–2022',
    rfidSupported: true,
    mode: 'Native Background NFC (No App Needed)',
    frequency: '13.56 MHz HF RFID / NFC (ISO 14443A / NTAG)',
    antennaLocation: 'Top back edge of the phone',
    antennaZone: 'top',
    instructions: 'Wake your iPhone screen and touch the top back edge directly to the Trophy Coin.'
  },
  {
    brand: 'Apple',
    model: 'iPhone X / iPhone 8 / iPhone 7 (iOS 14+)',
    years: '2016–2017',
    rfidSupported: true,
    mode: 'Control Center NFC Tag Reader',
    frequency: '13.56 MHz HF RFID / NFC (ISO 14443A / NTAG)',
    antennaLocation: 'Top back edge of the phone',
    antennaZone: 'top',
    instructions: 'Open iOS Control Center, tap the "NFC Tag Reader" icon, and hold the top edge of the iPhone against the Trophy Coin.'
  },
  {
    brand: 'Apple',
    model: 'iPhone 6s / 6 / SE (1st Gen) or older',
    years: '2015 & Earlier',
    rfidSupported: false,
    mode: 'Not Supported for NDEF Tag Reading',
    frequency: 'Apple Pay only (No general RFID/NFC tag reading)',
    antennaLocation: 'N/A',
    antennaZone: 'none',
    instructions: 'Older iPhones prior to iPhone 7 do not support reading NFC/RFID tags. You can still access your coin via its direct URL or QR code.'
  },
  // Samsung
  {
    brand: 'Samsung',
    model: 'Galaxy S24 / S23 / S22 / S21 / S20 Series (Standard, Plus, Ultra, FE)',
    years: '2020–Present',
    rfidSupported: true,
    mode: 'Native Android NFC + Chrome Web NFC',
    frequency: '13.56 MHz HF RFID / NFC (ISO 14443A / NTAG)',
    antennaLocation: 'Center of the back panel (just below the camera array)',
    antennaZone: 'center',
    instructions: 'Ensure NFC is turned ON in Quick Settings. Unlock your phone and hold the center of the back panel steady against the Trophy Coin.'
  },
  {
    brand: 'Samsung',
    model: 'Galaxy Z Fold & Z Flip Series (Fold 3/4/5/6, Flip 3/4/5/6)',
    years: '2021–Present',
    rfidSupported: true,
    mode: 'Native Android NFC + Chrome Web NFC',
    antennaLocation: 'Lower-center back half of the device',
    antennaZone: 'center',
    frequency: '13.56 MHz HF RFID / NFC (ISO 14443A / NTAG)',
    instructions: 'Ensure NFC is enabled in Settings > Connections > NFC. Hold the lower back half of the phone against the Trophy Coin.'
  },
  {
    brand: 'Samsung',
    model: 'Galaxy A55 / A54 / A53 / A35 / A34 / A25 / A15 (NFC Models)',
    years: '2021–Present',
    rfidSupported: true,
    mode: 'Native Android NFC + Chrome Web NFC',
    frequency: '13.56 MHz HF RFID / NFC (ISO 14443A / NTAG)',
    antennaLocation: 'Center back panel',
    antennaZone: 'center',
    instructions: 'Swipe down Quick Settings to verify the "NFC" tile is active, then hold the center back of the phone over the Trophy Coin.'
  },
  // Google Pixel
  {
    brand: 'Google',
    model: 'Pixel 9 / 9 Pro / 8 / 8 Pro / 7 / 7 Pro / 6 / 6 Pro / Fold',
    years: '2021–Present',
    rfidSupported: true,
    mode: 'Native Android NFC + Chrome Web NFC',
    frequency: '13.56 MHz HF RFID / NFC (ISO 14443A / NTAG)',
    antennaLocation: 'Center back panel (below the camera bar, near the G logo)',
    antennaZone: 'center',
    instructions: 'Unlock your Pixel, ensure NFC is enabled in Settings > Connected devices, and tap the Google "G" logo area on the back against the coin.'
  },
  // OnePlus / Xiaomi / Motorola / Other
  {
    brand: 'OnePlus',
    model: 'OnePlus 12 / 11 / 10 Pro / 9 / 8 / Nord Series',
    years: '2020–Present',
    rfidSupported: true,
    mode: 'Native Android NFC + Chrome Web NFC',
    frequency: '13.56 MHz HF RFID / NFC (ISO 14443A / NTAG)',
    antennaLocation: 'Upper-center back panel (around the camera module)',
    antennaZone: 'upper',
    instructions: 'Enable NFC in Settings, unlock the screen, and hold the upper-back area of the phone against the Trophy Coin.'
  },
  {
    brand: 'Xiaomi / Redmi / POCO',
    model: 'Xiaomi 14 / 13 / 12, Redmi Note Pro NFC, POCO F/X NFC Series',
    years: '2020–Present',
    rfidSupported: true,
    mode: 'Native Android NFC + Chrome Web NFC',
    frequency: '13.56 MHz HF RFID / NFC (ISO 14443A / NTAG)',
    antennaLocation: 'Upper-center back panel near camera module',
    antennaZone: 'upper',
    instructions: 'Ensure NFC is enabled in Settings > Connection & sharing > NFC, then hold the upper back of the phone to the Trophy Coin.'
  },
  {
    brand: 'Motorola / Sony / Asus / Nothing',
    model: 'Moto Edge / Razr, Sony Xperia, Asus Zenfone, Nothing Phone (1/2)',
    years: '2020–Present',
    rfidSupported: true,
    mode: 'Native Android NFC + Chrome Web NFC',
    frequency: '13.56 MHz HF RFID / NFC (ISO 14443A / NTAG)',
    antennaLocation: 'Center back panel',
    antennaZone: 'center',
    instructions: 'Turn on NFC in Settings, unlock your phone, and tap the center back of the device to the Trophy Coin.'
  }
];

function detectDeviceCapabilities() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const platform = typeof navigator !== 'undefined' ? navigator.platform || '' : '';
  const isSecure = typeof window !== 'undefined' ? Boolean(window.isSecureContext) : false;
  const hasWebNfc = typeof window !== 'undefined' && 'NDEFReader' in window;

  const isIOS = /iPhone|iPad|iPod/i.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isIPhone = /iPhone/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isMobile = isIOS || isAndroid || /Mobile/i.test(ua);

  let osName = 'Desktop OS';
  if (isIPhone) osName = 'Apple iOS (iPhone)';
  else if (isIOS) osName = 'Apple iPadOS / iOS';
  else if (isAndroid) osName = 'Google Android';
  else if (/Mac/i.test(platform)) osName = 'macOS (Desktop)';
  else if (/Win/i.test(platform)) osName = 'Windows (Desktop)';
  else if (/Linux/i.test(platform)) osName = 'Linux';

  let browserName = 'Web Browser';
  if (/CriOS/i.test(ua)) browserName = 'Chrome for iOS';
  else if (/EdgiOS|EdgA|Edg\//i.test(ua)) browserName = 'Microsoft Edge';
  else if (/OPR|Opera/i.test(ua)) browserName = 'Opera';
  else if (/SamsungBrowser/i.test(ua)) browserName = 'Samsung Internet';
  else if (/Chrome/i.test(ua)) browserName = 'Google Chrome';
  else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) browserName = 'Apple Safari';
  else if (/Firefox|FxiOS/i.test(ua)) browserName = 'Mozilla Firefox';

  let status = 'desktop';
  let headline = 'Desktop Browser Detected — Use Your Smartphone to Tap Physical Coins';
  let summary = 'Your computer does not have a built-in smartphone NFC antenna (unless a USB PC/SC NFC reader is attached). Select your phone model below to check its RFID compatibility!';
  let canNativeScan = false;

  if (isIPhone) {
    status = 'supported_ios';
    canNativeScan = true;
    headline = 'Your iPhone Supports Native TrophyCoin RFID / NFC Scanning!';
    summary = 'iPhones (iPhone 7 and newer on iOS 14+) read TrophyCoin 13.56 MHz RFID chips natively in the background. You do NOT need to open a browser scanner—just wake your screen and tap the top back edge of your iPhone to the coin.';
  } else if (isAndroid && hasWebNfc) {
    status = 'supported_web_nfc';
    canNativeScan = true;
    headline = 'Your Android Phone Supports Both Native & Live In-Browser RFID Scanning!';
    summary = 'Your device and browser support the Web NFC API (NDEFReader) as well as native Android background tag reading. You can run a live RFID tap test right now using the button below!';
  } else if (isAndroid) {
    status = 'supported_android_native';
    canNativeScan = true;
    headline = 'Your Android Device Supports Native OS RFID / NFC Scanning!';
    summary = 'Most Android phones read TrophyCoin 13.56 MHz RFID chips directly from the home screen when NFC is enabled in Settings. For in-browser live diagnostic scanning, open this page in Google Chrome for Android.';
  } else if (isIOS) {
    status = 'ipad';
    canNativeScan = false;
    headline = 'iPad Detected — Use an iPhone or Android Phone for RFID Tap Scanning';
    summary = 'Most iPads do not include an external NFC tag reader antenna. Use an iPhone (7 or newer) or NFC-enabled Android smartphone to tap physical Trophy Coins.';
  }

  return {
    osName,
    browserName,
    isSecure,
    hasWebNfc,
    isIOS,
    isIPhone,
    isAndroid,
    isMobile,
    status,
    canNativeScan,
    headline,
    summary
  };
}

export default function SupportPage({ user, tenantConfig, setActiveTab, setView }) {
  const actualUserRole = (user?.role || 'END_USER').toUpperCase();
  const isAdmin = actualUserRole === 'TENANT_ADMIN' || actualUserRole === 'GLOBAL_ADMIN';

  // Active role perspective (defaults to current user's role; can be switched to preview other user types)
  const [selectedRole, setSelectedRole] = useState(
    ROLE_META[actualUserRole] ? actualUserRole : 'END_USER'
  );

  useEffect(() => {
    if (user?.role && ROLE_META[user.role.toUpperCase()]) {
      setSelectedRole(user.role.toUpperCase());
    }
  }, [user?.role]);

  // Active Support section tab: 'assistant' | 'rfid' | 'guides'
  const [activeSection, setActiveSection] = useState('assistant');

  const roleInfo = ROLE_META[selectedRole] || ROLE_META.END_USER;

  // AI Assistant state
  const [chatHistory, setChatHistory] = useState([]);
  const [promptInput, setPromptInput] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Initialize or update greeting when role changes
  useEffect(() => {
    const meta = ROLE_META[selectedRole] || ROLE_META.END_USER;
    setChatHistory([
      {
        role: 'assistant',
        message: `Hello${user?.full_name ? ` ${user.full_name}` : ''}! I am your TrophyCoin Interface Support AI Agent, currently configured for the ${meta.label} (${meta.badge}) experience. Ask me how to do anything on the interface, or select a common question below!`,
        steps: [
          `Role Context: ${meta.summary}`,
          'Ask any question below for step-by-step instructions and direct 1-click navigation links.',
          'Switch to the "Phone RFID / NFC Checker" tab anytime to verify smartphone compatibility or test an RFID coin.'
        ],
        suggested_questions: meta.quickQuestions,
        quick_nav: null
      }
    ]);
  }, [selectedRole, user?.full_name]);

  const handleAskAi = async (questionText) => {
    const trimmed = (questionText ?? promptInput).trim();
    if (!trimmed || isAiLoading) return;

    const userMsg = { role: 'user', message: trimmed };
    const updatedHistory = [...chatHistory, userMsg];
    setChatHistory(updatedHistory);
    setPromptInput('');
    setIsAiLoading(true);

    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      const isDev = window.location.port === '3000';
      const API_BASE = isDev ? `http://${window.location.hostname}:8000` : '';
      const headers = {
        'Content-Type': 'application/json',
        'X-Tenant-Domain': window.location.hostname
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const compactHistory = updatedHistory.slice(-6).map(item => ({
        role: item.role === 'user' ? 'user' : 'model',
        content: item.message
      }));

      const res = await fetch(`${API_BASE}/api/v1/ai-designer/support-chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          prompt: trimmed,
          role: selectedRole,
          history: compactHistory,
          tenant_id: tenantConfig?.id || null
        })
      });

      if (!res.ok) {
        throw new Error(`Support request returned ${res.status}`);
      }

      const data = await res.json();
      setChatHistory(prev => [
        ...prev,
        {
          role: 'assistant',
          message: data.message || 'Here are the steps to accomplish that:',
          steps: Array.isArray(data.steps) ? data.steps : [],
          suggested_questions: Array.isArray(data.suggested_questions) ? data.suggested_questions : roleInfo.quickQuestions,
          quick_nav: data.quick_nav || null,
          source: data.source || 'ai'
        }
      ]);
    } catch (err) {
      setChatHistory(prev => [
        ...prev,
        {
          role: 'assistant',
          message: 'I can help you navigate directly to that feature. Use the Role Guides or Phone RFID Checker tabs above, or try one of the quick navigation buttons below.',
          steps: [
            'Check the "Role Guides & FAQs" tab for step-by-step instructions.',
            'Check the "Phone RFID / NFC Checker" tab to verify smartphone RFID compatibility.'
          ],
          suggested_questions: roleInfo.quickQuestions,
          quick_nav: { tab: 'overview', label: 'Go to Overview' }
        }
      ]);
    } finally {
      setIsAiLoading(false);
      setTimeout(() => {
        if (chatEndRef.current && typeof chatEndRef.current.scrollIntoView === 'function') {
          chatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 80);
    }
  };

  const handleQuickNavigate = (navTarget) => {
    if (!navTarget) return;
    if (navTarget === 'support_rfid') {
      setActiveSection('rfid');
      return;
    }
    if (typeof setView === 'function') {
      setView('landing');
    }
    if (typeof setActiveTab === 'function') {
      setActiveTab(navTarget);
    }
  };

  // Phone RFID / NFC Checker state
  const deviceCaps = useMemo(() => detectDeviceCapabilities(), []);
  const [selectedBrand, setSelectedBrand] = useState('All');
  const [phoneSearch, setPhoneSearch] = useState('');
  const [selectedPhoneIndex, setSelectedPhoneIndex] = useState(0);

  // Live NFC scan state
  const [nfcScanState, setNfcScanState] = useState('idle'); // 'idle' | 'scanning' | 'detected' | 'unsupported' | 'error'
  const [nfcScanResult, setNfcScanResult] = useState(null);
  const nfcAbortRef = useRef(null);

  useEffect(() => {
    return () => {
      if (nfcAbortRef.current) {
        try {
          nfcAbortRef.current.abort();
        } catch (_) {}
      }
    };
  }, []);

  const filteredPhones = useMemo(() => {
    return PHONE_DATABASE.filter(item => {
      const matchesBrand = selectedBrand === 'All' || item.brand.toLowerCase().includes(selectedBrand.toLowerCase());
      const q = phoneSearch.trim().toLowerCase();
      const matchesSearch = !q || item.brand.toLowerCase().includes(q) || item.model.toLowerCase().includes(q) || item.years.toLowerCase().includes(q);
      return matchesBrand && matchesSearch;
    });
  }, [selectedBrand, phoneSearch]);

  const activePhone = filteredPhones[selectedPhoneIndex] || filteredPhones[0] || PHONE_DATABASE[0];

  const handleStartLiveRfidTest = async () => {
    if (typeof window !== 'undefined' && 'NDEFReader' in window) {
      try {
        if (nfcAbortRef.current) {
          nfcAbortRef.current.abort();
        }
        const abortController = new AbortController();
        nfcAbortRef.current = abortController;

        setNfcScanState('scanning');
        setNfcScanResult({
          title: 'Ready to Scan — Hold Trophy Coin Against Your Phone',
          detail: 'Hold your physical Trophy Coin (13.56 MHz RFID / NFC tag) against the back of your phone now...'
        });

        const ndef = new window.NDEFReader();
        await ndef.scan({ signal: abortController.signal });

        ndef.onreadingerror = () => {
          setNfcScanState('error');
          setNfcScanResult({
            title: 'RFID Tag Detected, Could Not Read NDEF Data',
            detail: 'An RFID/NFC field was detected! Keep the coin steady against the antenna for 1–2 seconds and try again.'
          });
        };

        ndef.onreading = (event) => {
          const serialNumber = event.serialNumber || 'Verified UID';
          const recordsCount = event.message?.records?.length || 0;
          let decodedUrl = '';
          try {
            if (event.message?.records) {
              for (const rec of event.message.records) {
                if (rec.recordType === 'url' || rec.recordType === 'text') {
                  const decoder = new TextDecoder();
                  decodedUrl = decoder.decode(rec.data);
                  break;
                }
              }
            }
          } catch (_) {}

          setNfcScanState('detected');
          setNfcScanResult({
            title: 'Success! Your Phone Read the RFID / NFC Chip!',
            detail: `Tag UID: ${serialNumber} • NDEF Records: ${recordsCount}${decodedUrl ? ` • Payload: ${decodedUrl}` : ''}`
          });
        };
      } catch (err) {
        setNfcScanState('error');
        setNfcScanResult({
          title: 'Could Not Start Browser NFC Reader',
          detail: err?.message || 'Please ensure NFC is turned ON in your Android system settings and that you granted NFC permission to your browser.'
        });
      }
    } else if (deviceCaps.isIPhone) {
      setNfcScanState('detected');
      setNfcScanResult({
        title: 'iPhone Background RFID / NFC Reader is Built-In & Always Active!',
        detail: 'Apple iOS handles 13.56 MHz RFID / NFC coin scanning natively at the operating system level (no browser permission required). Simply hold the top back edge of your iPhone against any Trophy Coin right now — an iOS notification banner will pop up at the top of your screen!'
      });
    } else if (deviceCaps.isAndroid) {
      setNfcScanState('unsupported');
      setNfcScanResult({
        title: 'Use Native Android NFC or Open in Google Chrome',
        detail: 'Your Android phone can scan Trophy Coins directly from your home screen as long as NFC is toggled ON in Android Settings! To run an in-browser Web NFC diagnostic, open this page in Google Chrome for Android.'
      });
    } else {
      setNfcScanState('unsupported');
      setNfcScanResult({
        title: 'Desktop Browser Detected — Test With Your Smartphone',
        detail: 'Desktop computers do not include a built-in smartphone NFC antenna. Open this Support page on your iPhone or Android phone, or simply tap your physical Trophy Coin against the top edge of an iPhone or center-back of an Android phone!'
      });
    }
  };

  const handleStopLiveRfidTest = () => {
    if (nfcAbortRef.current) {
      try {
        nfcAbortRef.current.abort();
      } catch (_) {}
      nfcAbortRef.current = null;
    }
    setNfcScanState('idle');
    setNfcScanResult(null);
  };

  // Guide filter state
  const [guideSearch, setGuideSearch] = useState('');
  const filteredGuides = useMemo(() => {
    const q = guideSearch.trim().toLowerCase();
    if (!q) return roleInfo.guides;
    return roleInfo.guides.filter(
      g =>
        g.title.toLowerCase().includes(q) ||
        g.description.toLowerCase().includes(q) ||
        g.category.toLowerCase().includes(q) ||
        g.steps.some(s => s.toLowerCase().includes(q))
    );
  }, [roleInfo.guides, guideSearch]);

  return (
    <div
      data-testid="support-page-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        color: 'var(--text-main)',
        maxWidth: '1220px',
        margin: '0 auto',
        width: '100%'
      }}
    >
      {/* Top Hero Banner & Role Perspective Bar */}
      <div
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--border-color)',
          borderRadius: '12px',
          padding: '20px 24px',
          boxShadow: 'var(--card-shadow)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '16px'
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxWidth: '720px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span
                style={{
                  background: 'var(--badge-bg)',
                  color: 'var(--primary-color)',
                  border: '1px solid var(--border-color)',
                  padding: '4px 10px',
                  borderRadius: '999px',
                  fontSize: '11px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                {roleInfo.badge} Support
              </span>
              <span style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                Tailored for {roleInfo.label}
              </span>
            </div>
            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: 'var(--text-main)' }}>
              {tenantConfig?.brand_name || 'TrophyCoin'} Support &amp; AI Help Center
            </h2>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-mute)', lineHeight: '1.5' }}>
              {roleInfo.summary}
            </p>
          </div>

          {/* Role Perspective Switcher (always visible so users/admins can view guides for their role or preview other roles) */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              minWidth: '230px'
            }}
          >
            <label
              htmlFor="support-role-selector"
              style={{
                fontSize: '11px',
                fontWeight: '700',
                color: 'var(--text-mute)',
                textTransform: 'uppercase',
                letterSpacing: '0.4px'
              }}
            >
              {isAdmin ? 'Preview Support By User Type' : 'Support Role View'}
            </label>
            <select
              id="support-role-selector"
              data-testid="support-role-selector"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              style={{
                background: 'var(--input-bg)',
                color: 'var(--input-text)',
                border: '1px solid var(--input-border)',
                borderRadius: '8px',
                padding: '9px 12px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              {Object.values(ROLE_META).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label} ({r.badge}){r.id === actualUserRole ? ' — Your Role' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Section Navigation Tabs */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            borderTop: '1px solid var(--border-color)',
            paddingTop: '16px'
          }}
        >
          <button
            type="button"
            data-testid="support-tab-assistant"
            onClick={() => setActiveSection('assistant')}
            style={{
              background: activeSection === 'assistant' ? 'var(--primary-color)' : 'var(--button-secondary-bg)',
              color: activeSection === 'assistant' ? 'var(--button-primary-text, #ffffff)' : 'var(--button-secondary-text)',
              border: activeSection === 'assistant' ? '1px solid var(--primary-color)' : '1px solid var(--button-secondary-border)',
              borderRadius: '8px',
              padding: '10px 16px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.15s ease'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            Ask AI Interface Agent
          </button>

          <button
            type="button"
            data-testid="support-tab-rfid"
            onClick={() => setActiveSection('rfid')}
            style={{
              background: activeSection === 'rfid' ? 'var(--primary-color)' : 'var(--button-secondary-bg)',
              color: activeSection === 'rfid' ? 'var(--button-primary-text, #ffffff)' : 'var(--button-secondary-text)',
              border: activeSection === 'rfid' ? '1px solid var(--primary-color)' : '1px solid var(--button-secondary-border)',
              borderRadius: '8px',
              padding: '10px 16px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.15s ease'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
              <line x1="12" y1="18" x2="12.01" y2="18" />
              <path d="M8.5 8.5a5 5 0 0 1 7 0" />
              <path d="M10.2 11a2.5 2.5 0 0 1 3.6 0" />
            </svg>
            Phone RFID / NFC Compatibility Checker
          </button>

          <button
            type="button"
            data-testid="support-tab-guides"
            onClick={() => setActiveSection('guides')}
            style={{
              background: activeSection === 'guides' ? 'var(--primary-color)' : 'var(--button-secondary-bg)',
              color: activeSection === 'guides' ? 'var(--button-primary-text, #ffffff)' : 'var(--button-secondary-text)',
              border: activeSection === 'guides' ? '1px solid var(--primary-color)' : '1px solid var(--button-secondary-border)',
              borderRadius: '8px',
              padding: '10px 16px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.15s ease'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
            </svg>
            {roleInfo.badge} Step-by-Step Guides ({roleInfo.guides.length})
          </button>
        </div>
      </div>

      {/* SECTION 1: AI INTERFACE AGENT */}
      {activeSection === 'assistant' && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '20px',
            alignItems: 'start'
          }}
        >
          {/* Main AI Chat Panel */}
          <div
            style={{
              gridColumn: 'span 2',
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: '12px',
              boxShadow: 'var(--card-shadow)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid var(--border-color)',
                background: 'var(--panel-bg)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px',
                flexWrap: 'wrap'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div
                  style={{
                    width: '34px',
                    height: '34px',
                    borderRadius: '8px',
                    background: 'var(--primary-color)',
                    color: 'var(--button-primary-text, #ffffff)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '700'
                  }}
                >
                  AI
                </div>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-main)' }}>
                    Ask the TrophyCoin AI Agent
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                    Interactive interface walkthroughs for {roleInfo.label}s
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setActiveSection('rfid')}
                style={{
                  background: 'var(--button-secondary-bg)',
                  color: 'var(--button-secondary-text)',
                  border: '1px solid var(--button-secondary-border)',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Check Phone RFID Support →
              </button>
            </div>

            {/* Chat Messages Stream */}
            <div
              data-testid="support-ai-chat-history"
              style={{
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                maxHeight: '480px',
                overflowY: 'auto'
              }}
            >
              {chatHistory.map((item, idx) => {
                const isUser = item.role === 'user';
                return (
                  <div
                    key={idx}
                    style={{
                      alignSelf: isUser ? 'flex-end' : 'flex-start',
                      maxWidth: '88%',
                      background: isUser ? 'var(--primary-color)' : 'var(--panel-bg)',
                      color: isUser ? 'var(--button-primary-text, #ffffff)' : 'var(--text-main)',
                      border: isUser ? 'none' : '1px solid var(--border-color)',
                      borderRadius: '12px',
                      padding: '14px 16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px'
                    }}
                  >
                    <div style={{ fontSize: '14px', lineHeight: '1.55', fontWeight: isUser ? '500' : '400' }}>
                      {item.message}
                    </div>

                    {!isUser && Array.isArray(item.steps) && item.steps.length > 0 && (
                      <ol
                        style={{
                          margin: '4px 0 0 0',
                          paddingLeft: '20px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px',
                          fontSize: '13px',
                          color: 'var(--text-main)',
                          lineHeight: '1.5'
                        }}
                      >
                        {item.steps.map((step, sIdx) => (
                          <li key={sIdx}>{step}</li>
                        ))}
                      </ol>
                    )}

                    {!isUser && item.quick_nav && item.quick_nav.tab && (
                      <div style={{ paddingTop: '4px' }}>
                        <button
                          type="button"
                          onClick={() => handleQuickNavigate(item.quick_nav.tab)}
                          style={{
                            background: 'var(--primary-color)',
                            color: 'var(--button-primary-text, #ffffff)',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '8px 14px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <span>{item.quick_nav.label || `Go to ${item.quick_nav.tab}`}</span>
                          <span>→</span>
                        </button>
                      </div>
                    )}

                    {!isUser && Array.isArray(item.suggested_questions) && item.suggested_questions.length > 0 && idx === chatHistory.length - 1 && (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px',
                          marginTop: '6px',
                          paddingTop: '10px',
                          borderTop: '1px solid var(--border-color)'
                        }}
                      >
                        <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-mute)', textTransform: 'uppercase' }}>
                          Suggested Questions for {roleInfo.badge}:
                        </span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {item.suggested_questions.map((qText, qIdx) => (
                            <button
                              key={qIdx}
                              type="button"
                              onClick={() => handleAskAi(qText)}
                              disabled={isAiLoading}
                              style={{
                                background: 'var(--button-secondary-bg)',
                                color: 'var(--button-secondary-text)',
                                border: '1px solid var(--button-secondary-border)',
                                borderRadius: '999px',
                                padding: '6px 12px',
                                fontSize: '12px',
                                fontWeight: '500',
                                cursor: isAiLoading ? 'not-allowed' : 'pointer',
                                textAlign: 'left'
                              }}
                            >
                              {qText}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {isAiLoading && (
                <div
                  style={{
                    alignSelf: 'flex-start',
                    background: 'var(--panel-bg)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    fontSize: '13px',
                    color: 'var(--text-mute)'
                  }}
                >
                  AI Support Agent is preparing step-by-step instructions...
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Chat Input Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleAskAi(promptInput);
              }}
              style={{
                padding: '14px 20px',
                borderTop: '1px solid var(--border-color)',
                background: 'var(--panel-bg)',
                display: 'flex',
                gap: '10px'
              }}
            >
              <input
                type="text"
                data-testid="support-ai-input"
                value={promptInput}
                onChange={(e) => setPromptInput(e.target.value)}
                placeholder={`Ask how to do anything as a ${roleInfo.label} (e.g., "How do I mint a coin?" or "Is my iPhone supported?")...`}
                style={{
                  flex: 1,
                  background: 'var(--input-bg)',
                  color: 'var(--input-text)',
                  border: '1px solid var(--input-border)',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '13px',
                  outline: 'none'
                }}
              />
              <button
                type="submit"
                data-testid="support-ai-send-btn"
                disabled={isAiLoading || !promptInput.trim()}
                style={{
                  background: 'var(--primary-color)',
                  color: 'var(--button-primary-text, #ffffff)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 18px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: isAiLoading || !promptInput.trim() ? 'not-allowed' : 'pointer',
                  opacity: isAiLoading || !promptInput.trim() ? 0.65 : 1
                }}
              >
                Ask AI Agent
              </button>
            </form>
          </div>

          {/* Right Side Quick Tools Card: Phone RFID Summary + Role Shortcuts */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Phone RFID / NFC Quick Card */}
            <div
              style={{
                background: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: '12px',
                padding: '18px',
                boxShadow: 'var(--card-shadow)',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span
                  style={{
                    background: 'var(--badge-bg)',
                    color: 'var(--primary-color)',
                    border: '1px solid var(--border-color)',
                    padding: '3px 8px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '700',
                    textTransform: 'uppercase'
                  }}
                >
                  13.56 MHz RFID / NFC
                </span>
                <span style={{ fontSize: '12px', color: 'var(--text-mute)', fontWeight: '600' }}>
                  {deviceCaps.osName}
                </span>
              </div>

              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text-main)' }}>
                Is My Phone Supported for RFID Coin Scanning?
              </h3>

              <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-mute)', lineHeight: '1.5' }}>
                {deviceCaps.headline}
              </p>

              <button
                type="button"
                data-testid="open-rfid-checker-card-btn"
                onClick={() => setActiveSection('rfid')}
                style={{
                  background: 'var(--primary-color)',
                  color: 'var(--button-primary-text, #ffffff)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
              >
                Open Phone RFID Compatibility Checker →
              </button>
            </div>

            {/* Role Quick Navigation Card */}
            <div
              style={{
                background: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: '12px',
                padding: '18px',
                boxShadow: 'var(--card-shadow)',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}
            >
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: 'var(--text-main)' }}>
                {roleInfo.badge} Quick Workflows
              </h3>
              <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-mute)' }}>
                Jump straight to key features for your user type:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                {roleInfo.guides.map((g, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: 'var(--panel-bg)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      padding: '10px 12px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>
                        {g.title}
                      </span>
                      <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                        {g.category}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleQuickNavigate(g.actionTab)}
                      style={{
                        background: 'var(--button-secondary-bg)',
                        color: 'var(--button-secondary-text)',
                        border: '1px solid var(--button-secondary-border)',
                        borderRadius: '6px',
                        padding: '6px 10px',
                        fontSize: '11px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {g.actionLabel}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SECTION 2: PHONE RFID / NFC COMPATIBILITY CHECKER */}
      {activeSection === 'rfid' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Live Device Auto-Detection & Live Scan Test Card */}
          <div
            data-testid="rfid-live-device-card"
            style={{
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: '12px',
              padding: '22px',
              boxShadow: 'var(--card-shadow)',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
              <div>
                <span
                  style={{
                    background: 'var(--badge-bg)',
                    color: 'var(--primary-color)',
                    border: '1px solid var(--border-color)',
                    padding: '4px 10px',
                    borderRadius: '999px',
                    fontSize: '11px',
                    fontWeight: '700',
                    textTransform: 'uppercase'
                  }}
                >
                  Automatic Device Diagnostic
                </span>
                <h3 style={{ margin: '8px 0 4px 0', fontSize: '19px', fontWeight: '700', color: 'var(--text-main)' }}>
                  {deviceCaps.headline}
                </h3>
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-mute)', lineHeight: '1.5', maxWidth: '800px' }}>
                  {deviceCaps.summary}
                </p>
              </div>

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {nfcScanState !== 'scanning' ? (
                  <button
                    type="button"
                    data-testid="start-rfid-test-btn"
                    onClick={handleStartLiveRfidTest}
                    style={{
                      background: 'var(--primary-color)',
                      color: 'var(--button-primary-text, #ffffff)',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '11px 18px',
                      fontSize: '13px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M8.5 8.5a5 5 0 0 1 7 0" />
                      <path d="M6 6a8.5 8.5 0 0 1 12 0" />
                      <circle cx="12" cy="13" r="2" />
                    </svg>
                    Test My Device RFID / NFC Reader
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleStopLiveRfidTest}
                    style={{
                      background: 'var(--button-secondary-bg)',
                      color: 'var(--button-secondary-text)',
                      border: '1px solid var(--button-secondary-border)',
                      borderRadius: '8px',
                      padding: '11px 18px',
                      fontSize: '13px',
                      fontWeight: '700',
                      cursor: 'pointer'
                    }}
                  >
                    Stop Live RFID Scan
                  </button>
                )}
              </div>
            </div>

            {/* Detected Telemetry Grid */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '12px'
              }}
            >
              <div style={{ background: 'var(--panel-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-mute)', fontWeight: '700', textTransform: 'uppercase' }}>
                  Detected Operating System
                </div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-main)', marginTop: '4px' }}>
                  {deviceCaps.osName}
                </div>
              </div>

              <div style={{ background: 'var(--panel-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-mute)', fontWeight: '700', textTransform: 'uppercase' }}>
                  Detected Browser
                </div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-main)', marginTop: '4px' }}>
                  {deviceCaps.browserName}
                </div>
              </div>

              <div style={{ background: 'var(--panel-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-mute)', fontWeight: '700', textTransform: 'uppercase' }}>
                  Native OS RFID Tag Reading
                </div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--primary-color)', marginTop: '4px' }}>
                  {deviceCaps.canNativeScan ? 'Supported (13.56 MHz HF)' : 'Requires Smartphone'}
                </div>
              </div>

              <div style={{ background: 'var(--panel-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-mute)', fontWeight: '700', textTransform: 'uppercase' }}>
                  Web NFC Browser API
                </div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-main)', marginTop: '4px' }}>
                  {deviceCaps.hasWebNfc ? 'Available (NDEFReader)' : (deviceCaps.isIPhone ? 'Handled by iOS Background Reader' : 'Use Native OS Reader')}
                </div>
              </div>
            </div>

            {/* Live Scan Feedback Banner */}
            {nfcScanResult && (
              <div
                data-testid="rfid-test-result-banner"
                style={{
                  background: 'var(--panel-bg)',
                  border: '2px solid var(--primary-color)',
                  borderRadius: '10px',
                  padding: '14px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}
              >
                <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--primary-color)' }}>
                  {nfcScanResult.title}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-main)', lineHeight: '1.5' }}>
                  {nfcScanResult.detail}
                </div>
              </div>
            )}
          </div>

          {/* Interactive Phone Model Lookup & Antenna Placement Guide */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '20px',
              alignItems: 'start'
            }}
          >
            {/* Left: Searchable Phone Model Compatibility List */}
            <div
              style={{
                background: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: 'var(--card-shadow)',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px'
              }}
            >
              <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '17px', fontWeight: '700', color: 'var(--text-main)' }}>
                  Check Any Phone Model for RFID / NFC Compatibility
                </h3>
                <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-mute)' }}>
                  TrophyCoin embeds ISO 14443A / NTAG 13.56 MHz RFID chips inside physical coins. Select your phone below to verify compatibility and antenna location:
                </p>
              </div>

              {/* Brand Filter Pills + Search */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {['All', 'Apple', 'Samsung', 'Google', 'OnePlus', 'Xiaomi', 'Motorola'].map((brand) => (
                  <button
                    key={brand}
                    type="button"
                    onClick={() => {
                      setSelectedBrand(brand);
                      setSelectedPhoneIndex(0);
                    }}
                    style={{
                      background: selectedBrand === brand ? 'var(--primary-color)' : 'var(--button-secondary-bg)',
                      color: selectedBrand === brand ? 'var(--button-primary-text, #ffffff)' : 'var(--button-secondary-text)',
                      border: selectedBrand === brand ? '1px solid var(--primary-color)' : '1px solid var(--button-secondary-border)',
                      borderRadius: '999px',
                      padding: '5px 12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    {brand}
                  </button>
                ))}
              </div>

              <input
                type="text"
                data-testid="phone-compatibility-search"
                value={phoneSearch}
                onChange={(e) => {
                  setPhoneSearch(e.target.value);
                  setSelectedPhoneIndex(0);
                }}
                placeholder="Search phone model (e.g., iPhone 15, Galaxy S24, Pixel 8)..."
                style={{
                  background: 'var(--input-bg)',
                  color: 'var(--input-text)',
                  border: '1px solid var(--input-border)',
                  borderRadius: '8px',
                  padding: '10px 12px',
                  fontSize: '13px',
                  outline: 'none'
                }}
              />

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  maxHeight: '360px',
                  overflowY: 'auto'
                }}
              >
                {filteredPhones.map((item, idx) => {
                  const isSelected = item === activePhone;
                  return (
                    <button
                      key={`${item.brand}-${item.model}`}
                      type="button"
                      onClick={() => setSelectedPhoneIndex(idx)}
                      style={{
                        background: isSelected ? 'var(--panel-bg)' : 'transparent',
                        border: isSelected ? '2px solid var(--primary-color)' : '1px solid var(--border-color)',
                        borderRadius: '8px',
                        padding: '12px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '10px',
                        color: 'var(--text-main)'
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-main)' }}>
                          {item.brand} — {item.model}
                        </span>
                        <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                          {item.years} • {item.mode}
                        </span>
                      </div>
                      <span
                        style={{
                          background: 'var(--badge-bg)',
                          color: item.rfidSupported ? 'var(--primary-color)' : 'var(--text-mute)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '999px',
                          padding: '4px 10px',
                          fontSize: '11px',
                          fontWeight: '700',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {item.rfidSupported ? '✓ RFID Supported' : '✕ Not Supported'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right: Selected Phone Details & Visual Antenna Placement Diagram */}
            <div
              style={{
                background: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: 'var(--card-shadow)',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                <div>
                  <span
                    style={{
                      background: 'var(--badge-bg)',
                      color: activePhone.rfidSupported ? 'var(--primary-color)' : 'var(--text-mute)',
                      border: '1px solid var(--border-color)',
                      padding: '4px 10px',
                      borderRadius: '999px',
                      fontSize: '11px',
                      fontWeight: '700',
                      textTransform: 'uppercase'
                    }}
                  >
                    {activePhone.rfidSupported ? '100% Compatible with TrophyCoin RFID' : 'Requires QR / Direct Link'}
                  </span>
                  <h3 style={{ margin: '8px 0 2px 0', fontSize: '18px', fontWeight: '700', color: 'var(--text-main)' }}>
                    {activePhone.brand} {activePhone.model}
                  </h3>
                  <div style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                    Release Years: {activePhone.years}
                  </div>
                </div>
              </div>

              {/* Visual Phone Antenna Diagram */}
              <div
                style={{
                  background: 'var(--panel-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '10px',
                  padding: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '20px',
                  flexWrap: 'wrap'
                }}
              >
                {/* Stylized Phone Silhouette showing NFC Sweet Spot */}
                <div
                  style={{
                    width: '96px',
                    height: '176px',
                    borderRadius: '18px',
                    border: '2px solid var(--text-main)',
                    background: 'var(--card-bg)',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px',
                    boxSizing: 'border-box',
                    flexShrink: 0,
                    margin: '0 auto'
                  }}
                >
                  {/* Camera module indicator */}
                  <div
                    style={{
                      alignSelf: 'flex-start',
                      width: '26px',
                      height: '26px',
                      borderRadius: '6px',
                      border: '1px solid var(--border-color)',
                      background: 'var(--panel-bg)'
                    }}
                  />

                  {/* Highlighted RFID / NFC Antenna Zone */}
                  {activePhone.antennaZone !== 'none' && (
                    <div
                      title="RFID / NFC Antenna Sweet Spot"
                      style={{
                        position: 'absolute',
                        left: '10px',
                        right: '10px',
                        top:
                          activePhone.antennaZone === 'top'
                            ? '8px'
                            : activePhone.antennaZone === 'upper'
                            ? '34px'
                            : '64px',
                        height: '44px',
                        borderRadius: '10px',
                        border: '2px dashed var(--primary-color)',
                        background: 'var(--badge-bg)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: '700',
                        color: 'var(--primary-color)',
                        textAlign: 'center',
                        padding: '2px'
                      }}
                    >
                      TAP COIN HERE
                    </div>
                  )}

                  <div style={{ fontSize: '9px', color: 'var(--text-mute)', fontWeight: '600' }}>
                    BACK OF PHONE
                  </div>
                </div>

                <div style={{ flex: 1, minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-mute)', textTransform: 'uppercase' }}>
                      RFID / NFC Antenna Location
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--primary-color)', marginTop: '2px' }}>
                      {activePhone.antennaLocation}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-mute)', textTransform: 'uppercase' }}>
                      Supported RFID Frequency &amp; Protocol
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-main)', marginTop: '2px' }}>
                      {activePhone.frequency}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-mute)', textTransform: 'uppercase' }}>
                      How to Tap Your Trophy Coin
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-main)', marginTop: '2px', lineHeight: '1.5' }}>
                      {activePhone.instructions}
                    </div>
                  </div>
                </div>
              </div>

              {/* Troubleshooting Tips */}
              <div
                style={{
                  background: 'var(--panel-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  padding: '12px 14px',
                  fontSize: '12px',
                  color: 'var(--text-mute)',
                  lineHeight: '1.5'
                }}
              >
                <strong style={{ color: 'var(--text-main)' }}>Pro Scanning Tips:</strong> Hold the coin steady for 1–2 seconds rather than swiping quickly. Remove thick metal phone cases or magnetic wallets if the RFID field is blocked, and ensure your screen is awake.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SECTION 3: ROLE-SPECIFIC STEP-BY-STEP GUIDES */}
      {activeSection === 'guides' && (
        <div
          style={{
            background: 'var(--card-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: '12px',
            padding: '22px',
            boxShadow: 'var(--card-shadow)',
            display: 'flex',
            flexDirection: 'column',
            gap: '18px'
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
            <div>
              <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '700', color: 'var(--text-main)' }}>
                {roleInfo.label} ({roleInfo.badge}) Step-by-Step Interface Guides
              </h3>
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-mute)' }}>
                Detailed walkthroughs tailored to your user role with 1-click interface shortcuts.
              </p>
            </div>

            <input
              type="text"
              value={guideSearch}
              onChange={(e) => setGuideSearch(e.target.value)}
              placeholder="Filter guides..."
              style={{
                background: 'var(--input-bg)',
                color: 'var(--input-text)',
                border: '1px solid var(--input-border)',
                borderRadius: '8px',
                padding: '9px 14px',
                fontSize: '13px',
                minWidth: '240px',
                outline: 'none'
              }}
            />
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '16px'
            }}
          >
            {filteredGuides.map((guide, gIdx) => (
              <div
                key={gIdx}
                style={{
                  background: 'var(--panel-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '10px',
                  padding: '18px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: '14px'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span
                      style={{
                        background: 'var(--badge-bg)',
                        color: 'var(--primary-color)',
                        border: '1px solid var(--border-color)',
                        padding: '3px 8px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '700',
                        textTransform: 'uppercase'
                      }}
                    >
                      {guide.category}
                    </span>
                  </div>

                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text-main)' }}>
                    {guide.title}
                  </h4>

                  <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-mute)', lineHeight: '1.5' }}>
                    {guide.description}
                  </p>

                  <ol
                    style={{
                      margin: '4px 0 0 0',
                      paddingLeft: '18px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                      fontSize: '12px',
                      color: 'var(--text-main)',
                      lineHeight: '1.5'
                    }}
                  >
                    {guide.steps.map((st, sIdx) => (
                      <li key={sIdx}>{st}</li>
                    ))}
                  </ol>
                </div>

                <div style={{ paddingTop: '8px', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => handleQuickNavigate(guide.actionTab)}
                    style={{
                      background: 'var(--primary-color)',
                      color: 'var(--button-primary-text, #ffffff)',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 14px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    {guide.actionLabel} →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
